import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { X } from "lucide-react";
import * as React from "react";

import { CustomFieldRecord } from "@/shared/domain/customFields";
import { useDeleteCustomField } from "@/shared/domain/customFields/hooks";
import { Button } from "@/shared/foundation/primitives";
import { RobotoDomainException } from "@/shared/http";
import { isDeleteConfirmed } from "@/shared/text";

import styles from "./customFieldDialog.module.css";

interface DeleteCustomFieldDialogProps {
  // The field to delete; `null` keeps the dialog closed.
  field: CustomFieldRecord | null;
  onClose: () => void;
}

export function DeleteCustomFieldDialog({
  field,
  onClose,
}: DeleteCustomFieldDialogProps) {
  const deleteCustomField = useDeleteCustomField();
  const [confirmText, setConfirmText] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // Clear the transient state each time the dialog opens on a field.
  React.useEffect(() => {
    if (field) {
      setConfirmText("");
      setSubmitError(null);
    }
  }, [field]);

  const confirmed = field
    ? isDeleteConfirmed(confirmText, field.field_name)
    : false;

  const onConfirm = async () => {
    if (!field || !confirmed) {
      return;
    }
    setSubmitError(null);
    try {
      await deleteCustomField.mutateAsync({
        fieldId: field.field_id,
        orgId: field.org_id,
        entityType: field.entity_type,
      });
      onClose();
    } catch (error) {
      // Stay open and surface the failure in-dialog so the user can retry.
      let errorMessage = "Failed to delete custom field. Please try again.";
      if (error instanceof RobotoDomainException) {
        errorMessage = error.message;
      }

      setSubmitError(errorMessage);
    }
  };

  return (
    <Dialog
      className={styles.dialog}
      open={field !== null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={styles.title}>
        Delete custom field
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={styles.content}>
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}

        <DialogContentText>
          Deleting <code>{field?.field_name}</code> permanently removes the
          custom field and its values on Roboto entities. This can&apos;t be
          undone.
        </DialogContentText>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Type <code>{field?.field_name}</code> to confirm
          </span>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && confirmed) {
                event.preventDefault();
                void onConfirm();
              }
            }}
          />
        </div>
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Button variant="outlined" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void onConfirm()}
          loading={deleteCustomField.isPending}
          disabled={!confirmed || deleteCustomField.isPending}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
